// accessControl.ts - Clean role-based permission system
// -------------------------------------------------------------------------
// Role codes: 'admin' = Company Admin, 'user' = Standard User, 'viewer' = View Only
// Access control based on User.isMaster flag and UserCompanyMembership.role.code

// Type definitions
interface RoutePermissions {
    [key: string]: {
        roles?: string[];        // Role codes that can access (empty = no role access)
        isMasterOnly?: boolean;  // Only users with isMaster flag can access
    };
}

// Route-based permissions
// -------------------------------------------------------
// This object controls permissions to different routes based on roles and master status
// Role codes: 'admin' = Company Admin, 'user' = Standard User, 'viewer' = View Only
export const routePermissions: RoutePermissions = {
    // Master-only routes
    '/assistant': { isMasterOnly: true },
    '/back-office': { isMasterOnly: true },
    '/back-office/metrics/new': { isMasterOnly: true },
    '/back-office/metrics/:id/edit': { isMasterOnly: true },
    '/back-office/area-types/new': { isMasterOnly: true },
    '/back-office/area-types/:id/edit': { isMasterOnly: true },
    '/report-templates/settings': { isMasterOnly: true },
    '/area-types/new': { isMasterOnly: true },
    '/area-types/:id/edit': { isMasterOnly: true },
    
    // Admin-only routes
    '/configuration': { roles: ['admin'] },
    '/sales-dashboard': { roles: ['admin'] },
    '/operations': { roles: ['admin'] },
    '/areas/new': { roles: ['admin'] },
    '/areas/:id/edit': { roles: ['admin'] },
    '/patterns/create/template': { roles: ['admin'] },
    '/patterns/create/area': { roles: ['admin'] },
    
    // Admin + User routes (Viewer excluded)
    '/areas': { roles: ['admin', 'user'] },
    '/patterns': { roles: ['admin', 'user'] },
    '/measurements': { roles: ['admin', 'user'] },
    '/measurements/:id': { roles: ['admin', 'user'] },
    '/measurements/assign-areas': { roles: ['admin', 'user'] },
    '/measurements/assign-patterns': { roles: ['admin', 'user'] },
    '/measurements/:id/edit-location': { roles: ['admin', 'user'] },
    '/measurement-list': { roles: ['admin', 'user'] },
    '/devices': { roles: ['admin', 'user'] },
    '/report-templates/wizard': { roles: ['admin', 'user'] },
    
    // Admin + User
    '/dashboard': { roles: ['admin', 'user'] },
    '/reports': { roles: ['admin', 'user'] },
};

// Access control functions
// -----------------------

/**
 * Check if a user can access a route based on their master status and role code
 * @param isMaster - User's isMaster flag
 * @param userRoleCode - User's role code from UserCompanyMembership (e.g. 'admin', 'user', 'viewer')
 * @param route - The route path to check
 * @returns boolean indicating if user has access
 */
export const canAccessRoute = (
    isMaster: boolean, 
    userRoleCode: string | undefined, 
    route: string
): boolean => {
    // Master users can access everything
    if (isMaster) {
        return true;
    }
    
    // Find matching route permission (check for exact match or dynamic route pattern)
    let permission = routePermissions[route];
    
    // If no exact match, try to match dynamic routes (e.g., /areas/:id)
    if (!permission) {
        const matchingRoute = Object.keys(routePermissions).find(r => {
            const regex = new RegExp(`^${r.replace(/:\w+/g, '\\w+')}$`);
            return regex.test(route);
        });
        
        if (matchingRoute) {
            permission = routePermissions[matchingRoute];
        }
    }
    
    // If no permission defined, allow access by default
    if (!permission) {
        return true;
    }
    
    // Check master-only restriction
    if (permission.isMasterOnly) {
        return false; // Non-master users cannot access
    }
    
    // Check role-based permissions
    if (permission.roles) {
        // If role code is missing, deny access
        if (!userRoleCode) {
            return false;
        }
        
        // Check if the user's role code is in the allowed list
        return permission.roles.includes(userRoleCode);
    }
    
    // If we got here, allow access
    return true;
};

/**
 * Get a safe default route for a user based on their role/master status.
 * This avoids redirect loops when a user can't access /dashboard.
 */
export const getDefaultRoute = (
    isMaster: boolean,
    userRoleCode: string | undefined
): string => {
    const preferredRoutes = [
        '/dashboard',
        '/saved-reports',
        '/areas',
        '/patterns',
        '/measurements',
        '/reports',
        '/configuration',
    ];

    for (const route of preferredRoutes) {
        if (canAccessRoute(isMaster, userRoleCode, route)) {
            return route;
        }
    }

    return '/select-company';
};

/**
 * Check if a user is a master user
 * @param user - The user object
 * @returns boolean indicating if user is master
 */
export const isMasterUser = (user: any): boolean => {
    return user?.isMaster === true;
};

/**
 * Get the user's role code from their current company membership
 * @param user - The user object with company memberships
 * @returns role code string (e.g. 'admin', 'user', 'viewer') or undefined
 */
export const getUserRoleCode = (user: any): string | undefined => {
    if (!user) return undefined;

    // Primary: currentCompanyRole (set by CompanySelection, preserved by AuthContext refresh)
    if (user.currentCompanyRole?.code) {
        return user.currentCompanyRole.code;
    }

    // Fallback: find role from companyMemberships array
    const currentCompanyId = user.currentCompanyId || user.companyId;
    const memberships = user.companyMemberships;

    if (Array.isArray(memberships) && memberships.length > 0) {
        const activeMembership = memberships.find(
            (m: any) => m?.company?.id === currentCompanyId
        ) || memberships[0];

        return activeMembership?.role?.code;
    }

    return undefined;
};

/**
 * Check if a route should be visible in the top navigation bar
 * @param isMaster - User's isMaster flag
 * @param userRoleCode - User's role code
 * @param route - The route path to check
 * @returns boolean indicating if route should be visible
 */
export const canViewInTopBar = (
    isMaster: boolean,
    userRoleCode: string | undefined,
    route: string
): boolean => {
    return canAccessRoute(isMaster, userRoleCode, route);
};

/**
 * Check if user has permission for a specific action/feature
 * @param isMaster - User's isMaster flag
 * @param userRoleCode - User's role code
 * @param action - Action identifier (e.g., 'create', 'edit', 'delete', 'create_area', 'create_pattern')
 * @returns boolean indicating if user has permission
 */
export const hasPermission = (
    isMaster: boolean,
    userRoleCode: string | undefined,
    action: string
): boolean => {
    // Master has all permissions
    if (isMaster) {
        return true;
    }
    
    // Define action permissions using role codes
    const actionPermissions: { [key: string]: string[] } = {
        // General permissions
        'view': ['admin', 'user', 'viewer'], // All roles can view
        'delete': ['admin'],                 // Admin only can delete
        
        // Area-specific permissions
        'create_area': ['admin'],
        'edit_area': ['admin'],
        'delete_area': ['admin'],
        
        // Pattern-specific permissions
        'create_pattern': ['admin'],
        'edit_pattern': ['admin'],
        'delete_pattern': ['admin'],
        
        // Measurement permissions
        'create_measurement': ['admin', 'user'],
        'edit_measurement': ['admin', 'user'],
        'delete_measurement': ['admin'],
        
        // Generic create/edit
        'create': ['admin', 'user'],
        'edit': ['admin', 'user'],
    };
    
    const allowedRoles = actionPermissions[action];
    
    if (!allowedRoles) {
        return false;
    }
    
    if (!userRoleCode) {
        return false;
    }
    
    return allowedRoles.includes(userRoleCode);
};
